// Get current active tenant ID from sessionStorage
const getActiveTenantId = () => {
  return sessionStorage.getItem('activeTenantId') || 'default';
};

// Create tenant-scoped cache key
const getTenantLockedFeaturesCacheKey = (tenantId) => {
  return `lockedTenantFeatures:${tenantId}`;
};

const getTenantFeatureAccessCacheKey = (tenantId) => {
  return `tenantFeatureAccess:${tenantId}`;
};

const normalizeFeatureName = (featureName) => {
  return (featureName || '').toString().trim().toLowerCase();
};

// Parse locked features for specific tenant
const parseLockedFeatures = (tenantId) => {
  try {
    const cacheKey = getTenantLockedFeaturesCacheKey(tenantId);
    const raw = sessionStorage.getItem(cacheKey);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Save locked features for specific tenant
const saveLockedFeatures = (features, tenantId) => {
  const cacheKey = getTenantLockedFeaturesCacheKey(tenantId);
  sessionStorage.setItem(cacheKey, JSON.stringify(features));
};

const parseFeatureAccess = (tenantId) => {
  try {
    const raw = sessionStorage.getItem(getTenantFeatureAccessCacheKey(tenantId));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * Get all locked features for the current tenant
 * @returns {Array} Array of locked feature objects with featureName, endpoint, and lockedAt
 */
export const getLockedTenantFeatures = () => {
  const tenantId = getActiveTenantId();
  return parseLockedFeatures(tenantId);
};

/**
 * Mark a feature as locked for the current tenant
 * @param {Object} options - { featureName, endpoint }
 * @returns {Array} Updated locked features array
 */
export const markTenantFeatureAsLocked = ({ featureName, endpoint }) => {
  const tenantId = getActiveTenantId();
  const normalizedFeature = normalizeFeatureName(featureName);
  if (!normalizedFeature) return parseLockedFeatures(tenantId);

  const current = parseLockedFeatures(tenantId);
  const withoutCurrent = current.filter(
    (item) => normalizeFeatureName(item.featureName) !== normalizedFeature
  );

  const next = [
    ...withoutCurrent,
    {
      featureName,
      endpoint: endpoint || '',
      lockedAt: new Date().toISOString(),
    },
  ];

  saveLockedFeatures(next, tenantId);
  setTenantFeatureAccess(featureName, false, tenantId);
  return next;
};

/**
 * Cache an explicit feature-access result for this browser session. Access is tenant-scoped so
 * switching organizations can never reuse another tenant's add-on state.
 */
export const setTenantFeatureAccess = (featureName, enabled, tenantId = getActiveTenantId()) => {
  const normalizedFeature = normalizeFeatureName(featureName);
  if (!normalizedFeature || typeof enabled !== 'boolean') return;

  const current = parseFeatureAccess(tenantId);
  sessionStorage.setItem(
    getTenantFeatureAccessCacheKey(tenantId),
    JSON.stringify({ ...current, [normalizedFeature]: enabled })
  );
};

/** Seed navigation before rendering the authenticated workspace. Only explicit booleans count;
 * an omitted feature remains unknown so older API responses still use the access check. */
export const cacheSessionFeatureAccess = (features, tenantId) => {
  if (!tenantId || !features || typeof features !== 'object') return;
  const entries = Array.isArray(features)
    ? features.map((feature) => [feature?.name ?? feature?.Name, feature?.enabled ?? feature?.Enabled])
    : Object.entries(features);

  entries.forEach(([name, enabled]) => {
    const normalizedName = normalizeFeatureName(name);
    if (!normalizedName || typeof enabled !== 'boolean') return;
    // A fresh session response supersedes a denial from an earlier session.
    saveLockedFeatures(parseLockedFeatures(tenantId).filter(
      (item) => normalizeFeatureName(item.featureName) !== normalizedName
    ), tenantId);
    setTenantFeatureAccess(name, enabled, tenantId);
  });
};

/**
 * Return true/false only when access has been established; null means unknown. Consumers can
 * fail closed while a silent entitlement check is in flight, avoiding a flash of unavailable UI.
 */
export const getTenantFeatureAccess = (featureName, tenantId = getActiveTenantId()) => {
  const normalizedFeature = normalizeFeatureName(featureName);
  if (!normalizedFeature) return null;
  if (parseLockedFeatures(tenantId).some(
    (item) => normalizeFeatureName(item.featureName) === normalizedFeature
  )) return false;

  const access = parseFeatureAccess(tenantId);
  return typeof access[normalizedFeature] === 'boolean' ? access[normalizedFeature] : null;
};

/**
 * Check if a feature is locked for the current tenant
 * @param {string} featureName - Name of the feature to check
 * @returns {boolean} True if feature is locked, false otherwise
 */
export const isTenantFeatureLocked = (featureName) => {
  const tenantId = getActiveTenantId();
  const normalizedFeature = normalizeFeatureName(featureName);
  if (!normalizedFeature) return false;

  return parseLockedFeatures(tenantId).some(
    (item) => normalizeFeatureName(item.featureName) === normalizedFeature
  );
};

/**
 * Clear all locked features for a specific tenant
 * Called when switching tenants to ensure clean state
 * @param {string} tenantId - Tenant ID to clear locked features for
 */
export const clearTenantLockedFeatures = (tenantId) => {
  if (!tenantId) return;
  const cacheKey = getTenantLockedFeaturesCacheKey(tenantId);
  sessionStorage.removeItem(cacheKey);
  sessionStorage.removeItem(getTenantFeatureAccessCacheKey(tenantId));
};

/**
 * Clear locked features for all tenants
 * Useful for logout scenarios
 */
export const clearAllLockedFeatures = () => {
  const keys = Object.keys(sessionStorage);
  keys.forEach((key) => {
    if (key.startsWith('lockedTenantFeatures:') || key.startsWith('tenantFeatureAccess:')) {
      sessionStorage.removeItem(key);
    }
  });
};

/**
 * Navigation items flagged `hideWhenLocked` are removed, not shown disabled, once the tenant is
 * known to lack the feature (an add-on the tenant has not bought should be absent from the menu).
 * `isLocked` is injected so this stays a pure function.
 */
export const filterHiddenWhenLocked = (items, isLocked) =>
  items.filter((item) => !(item.hideWhenLocked && isLocked(item)));
